import { useSignIn, useSignUp } from '@clerk/clerk-react';
import { useState } from 'react';

import { OAUTH_PROVIDERS, OAuthProvider } from '@/constants/auth-providers';
import { markAuthMethodAttempt } from '@/helpers/auth-method.helper';
import { orgUrl } from '@/helpers/org-path.helper';
import { toast } from '@/hooks/ui/use-toast';
import logger from '@/services/logger.service';

interface UseOAuthSignInOptions {
  /** 'sign-up' routes through Clerk's signUp resource (invitations, new accounts). */
  flow?: 'sign-in' | 'sign-up';
  /** Where Clerk lands the user once the provider hands them back. */
  redirectUrlComplete?: string;
  /** Lets Clerk finish a partially filled sign-up after the provider returns. */
  continueSignUp?: boolean;
}

export interface UseOAuthSignInReturn {
  pendingProvider: OAuthProvider | null;
  authenticateWith: (provider: OAuthProvider) => Promise<void>;
}

export const useOAuthSignIn = ({
  flow = 'sign-in',
  redirectUrlComplete,
  continueSignUp = false,
}: UseOAuthSignInOptions = {}): UseOAuthSignInReturn => {
  // state
  const [pendingProvider, setPendingProvider] = useState<OAuthProvider | null>(
    null
  );

  // custom hooks
  const { isLoaded: isSignInLoaded, signIn } = useSignIn();
  const { isLoaded: isSignUpLoaded, signUp } = useSignUp();

  // derived values
  const isLoaded = flow === 'sign-up' ? isSignUpLoaded : isSignInLoaded;
  const resource = flow === 'sign-up' ? signUp : signIn;

  // event handlers
  const authenticateWith = async (provider: OAuthProvider) => {
    if (!isLoaded || !resource) {
      toast({
        variant: 'destructive',
        title: 'Sign in is not ready yet',
        description: 'Give it a moment and try again',
      });
      return;
    }

    const { strategy, name } = OAUTH_PROVIDERS[provider];

    setPendingProvider(provider);
    try {
      // Only an attempt so far - ProtectedRoute confirms it once a session
      // exists, so bouncing off the provider does not leave a false badge.
      markAuthMethodAttempt(provider);
      await resource.authenticateWithRedirect({
        strategy,
        continueSignUp,
        redirectUrl: orgUrl('/auth/sso-callback'),
        redirectUrlComplete: redirectUrlComplete ?? orgUrl('/auth/join-org'),
      });
      // On success the browser navigates away, so the spinner is intentionally
      // left running - clearing it here would flash the button back to idle.
    } catch (error) {
      logger.error('OAuth authentication failed', error as Error, {
        strategy,
        flow,
      });
      toast({
        variant: 'destructive',
        title: `Could not reach ${name}`,
        description: 'Please try again',
      });
      setPendingProvider(null);
    }
  };

  return { pendingProvider, authenticateWith };
};
